import { motion } from "framer-motion";
import Seo from "@/components/seo/Seo";
import { MapPin, Target, Users } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { COMPANY } from "@/config/company";

const team = [
  { name: "Srivalli Chilukuri", role: "Director" },
  { name: "Aditya Prasad", role: "Chief Sending Officer" },
  { name: "Ravi Teja", role: "Operations Manager" },
  { name: "Siva", role: "Customer Service Officer" },
];

const locations = [
  {
    title: "Registered office",
    lines: [COMPANY.legalName, ...COMPANY.addressLines, `CIN: ${COMPANY.cin}`],
  },
  {
    title: "Sendtoo – Bharat CourierPoint",
    lines: ["49-34-8, Akkayyapalem Main Road", "opp. Coastal Bank, NGGO Colony", "Akkayyapalem, Visakhapatnam – 530016, AP"],
  },
];

export default function About() {
  return (
    <PublicLayout>
      <Seo title={"About Sendtoo"} description={"Sendtoo is a brand of Capexlite Logistics Solutions, Visakhapatnam - a South-India logistics operator with its own AP & Telangana network and national partner reach."} path={"/about"} />
      <div className="min-h-screen">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#0A183D] to-[#1B3FA0] py-20">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white">About Sendtoo</h1>
            <p className="text-blue-200 mt-5 text-lg max-w-2xl mx-auto leading-relaxed">
              Sendtoo is a brand of Capexlite Logistics Solutions Private Limited, the operating company behind it: a Visakhapatnam-built logistics company running its own delivery network across South India — extending to all of India and beyond through trusted partners.
            </p>
          </div>
        </div>

        {/* Mission */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-10">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Target className="w-7 h-7 text-brand-500 mb-4" strokeWidth={1.5} />
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-3">What we're building</h3>
              <p className="text-slate-600 leading-relaxed">
                A logistics network that actually holds to the delivery window it promises — 24 to 48 hours across South India, on our own network, with the same discipline extended to every partner route beyond it.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} viewport={{ once: true }}>
              <Users className="w-7 h-7 text-brand-500 mb-4" strokeWidth={1.5} />
              <h3 className="font-heading font-semibold text-xl text-slate-900 mb-3">How we work</h3>
              <p className="text-slate-600 leading-relaxed">
                Elastic and tailor-made — every shipment is sized to what it actually needs, not forced into a fixed slot. Individuals, small businesses, and companies shipping regularly all move through the same platform.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-12 text-center">The team</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {team.map((member, i) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-lg p-6 text-center"
                >
                  <div className="w-14 h-14 bg-brand-500 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-heading font-bold text-xl">
                    {member.name[0]}
                  </div>
                  <h4 className="font-semibold text-slate-800 text-sm">{member.name}</h4>
                  <p className="text-xs text-slate-500 mt-0.5">{member.role}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Locations */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-12 text-center">Where to find us</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {locations.map((loc) => (
                <div key={loc.title} className="bg-slate-50 rounded-lg p-6 flex gap-4">
                  <MapPin className="w-5 h-5 text-brand-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <h4 className="font-heading font-semibold text-slate-900 mb-1.5">{loc.title}</h4>
                    {loc.lines.map((line, j) => (
                      <p key={j} className="text-sm text-slate-600">{line}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}
